import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { StrapiCustomerRow } from '../../../../../api/strapiCrm';
import type { StrapiManagedUserRow } from '../../../../../api/strapiUsers';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { useUsersQuery } from '../../../../../query/hooks';
import type { StrapiCompanyBranchRow } from '../../../../../api/strapiCrm';
import { formatCompanyBranchLabel } from '../../../../../api/strapiCrm';
import { CustomerSearchAutocomplete } from '../../../../../components/CustomerSearchAutocomplete';
import { QUOTATION_STATUS_OPTIONS } from '../../quotationFormConstants';
import { quotationStatusDisplayLabel } from '../../../quotationWorkflow';
import { useCompanyBranchesQuery } from '../../../../../query/hooks';
import type { QuotationFormValues } from '../../quotationFormTypes';

type Props = {
  jwt: string | null;
  quotation_number: string;
  quotation_date: string;
  company_branch: StrapiCompanyBranchRow | null;
  companyBranchError?: string;
  customer: StrapiCustomerRow | null;
  preorder: boolean;
  progress_status: QuotationFormValues['progress_status'];
  /** Status paralel permintaan revisi (untuk label readonly). */
  edit_request_status?: 'none' | 'pending' | 'approved' | 'rejected' | null;
  formReadOnly?: boolean;
  hideStatusField?: boolean;
  is_contract: boolean;
  is_consultant_quote: boolean;
  contract_period_start_date: string;
  contract_period_end_date: string;
  contractPeriodStartDateError?: string;
  contractPeriodEndDateError?: string;
  onAddSampleGroup: () => void;
  onPatchContractFlags: (
    patch: Partial<Pick<QuotationFormValues, 'is_contract' | 'is_consultant_quote'>>,
  ) => void;
  sample_pickup_method: QuotationFormValues['sample_pickup_method'];
  assignee_user: StrapiManagedUserRow | null;
  approved_by_user: StrapiManagedUserRow | null;
  term_of_payment: boolean;
  term_of_payment_date: string;
  term_of_payment_note: string;
  termOfPaymentDateError?: string;
  onPatch: (patch: Partial<QuotationFormValues>) => void;
  customerError?: string;
  quotationNumberError?: string;
  quotationNumberDisabled: boolean;
  quotationNumberFreeze?: boolean;
  hideQuotationNumberInput?: boolean;
  customerSelectDisabled?: boolean;
  quotationNumberTestId?: string;
};

const SEARCH_DEBOUNCE_MS = 320;
const minUserSearchLen = 2;

function dedupeUsers(rows: StrapiManagedUserRow[]): StrapiManagedUserRow[] {
  const seen = new Set<string>();
  const out: StrapiManagedUserRow[] = [];
  for (const r of rows) {
    const k = r.documentId ?? r.id;
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

function userOptionLabel(u: StrapiManagedUserRow): string {
  const name = u.username?.trim() || u.email?.trim() || u.id;
  const em = u.email?.trim();
  return em ? `${name} — ${em}` : name;
}

/** Tab detail: nomor quotation, cabang, pelanggan, kontrak/konsultan, status/preorder, pickup, PIC penanggung jawab & persetujuan. */
export function QuotationFormDetailTabScreen({
  jwt,
  quotation_number,
  quotation_date,
  company_branch,
  companyBranchError,
  customer,
  preorder,
  progress_status,
  edit_request_status = null,
  formReadOnly = false,
  hideStatusField = false,
  is_contract,
  is_consultant_quote,
  contract_period_start_date,
  contract_period_end_date,
  contractPeriodStartDateError,
  contractPeriodEndDateError,
  onAddSampleGroup,
  onPatchContractFlags,
  sample_pickup_method,
  assignee_user,
  approved_by_user,
  term_of_payment,
  term_of_payment_date,
  term_of_payment_note,
  termOfPaymentDateError,
  onPatch,
  customerError,
  quotationNumberError,
  quotationNumberDisabled,
  quotationNumberFreeze = false,
  hideQuotationNumberInput = false,
  customerSelectDisabled = false,
  quotationNumberTestId = 'quotation-form-number',
}: Props) {
  const usesSampleGroups = is_contract || is_consultant_quote;

  const [assigneeInput, setAssigneeInput] = useState('');
  const [approverInput, setApproverInput] = useState('');

  const debouncedAssigneeSearch = useDebouncedValue(assigneeInput, SEARCH_DEBOUNCE_MS);
  const debouncedApproverSearch = useDebouncedValue(approverInput, SEARCH_DEBOUNCE_MS);

  const companyBranchesQ = useCompanyBranchesQuery(jwt);
  const branchOptions = companyBranchesQ.data?.rows ?? [];

  const assigneeSearchOk = debouncedAssigneeSearch.trim().length >= minUserSearchLen;
  const assigneeUsersQ = useUsersQuery(jwt, {
    emailContains: '',
    searchContains: assigneeSearchOk ? debouncedAssigneeSearch.trim() : undefined,
    status: 'active',
  });

  const approverSearchOk = debouncedApproverSearch.trim().length >= minUserSearchLen;
  const approverUsersQ = useUsersQuery(jwt, {
    emailContains: '',
    searchContains: approverSearchOk ? debouncedApproverSearch.trim() : undefined,
    status: 'active',
  });

  useEffect(() => {
    if (!assignee_user) {
      setAssigneeInput('');
      return;
    }
    setAssigneeInput(userOptionLabel(assignee_user));
  }, [assignee_user?.documentId, assignee_user?.id]);

  useEffect(() => {
    if (!approved_by_user) {
      setApproverInput('');
      return;
    }
    setApproverInput(userOptionLabel(approved_by_user));
  }, [approved_by_user?.documentId, approved_by_user?.id]);

  const assigneeOptions = useMemo(() => {
    const fromApi = assigneeSearchOk ? (assigneeUsersQ.data?.rows ?? []) : [];
    const merged = [...(assignee_user ? [assignee_user] : []), ...fromApi];
    return dedupeUsers(merged);
  }, [assignee_user, assigneeSearchOk, assigneeUsersQ.data?.rows]);

  const approverOptions = useMemo(() => {
    const fromApi = approverSearchOk ? (approverUsersQ.data?.rows ?? []) : [];
    const merged = [...(approved_by_user ? [approved_by_user] : []), ...fromApi];
    return dedupeUsers(merged);
  }, [approved_by_user, approverSearchOk, approverUsersQ.data?.rows]);

  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      {hideQuotationNumberInput ? (
        <Typography variant="body2" color="text.secondary">
          Nomor quotation dibuat otomatis setelah penyimpanan (format QUO.DDMMYY.#####, urutan per hari menurut zona
          WIB).
        </Typography>
      ) : (
        <TextField
          label="Nomor quotation"
          required
          fullWidth
          value={quotation_number}
          onChange={(e) => onPatch({ quotation_number: e.target.value })}
          disabled={quotationNumberDisabled || quotationNumberFreeze}
          autoComplete="off"
          error={Boolean(quotationNumberError)}
          inputProps={{ 'data-testid': quotationNumberTestId }}
          helperText={
            quotationNumberError
              ? quotationNumberError
              : quotationNumberDisabled
                ? 'Nomor quotation tidak dapat diubah.'
                : undefined
          }
        />
      )}

      <TextField
        label="Tanggal quotation"
        type="date"
        fullWidth
        value={quotation_date}
        onChange={(e) => onPatch({ quotation_date: e.target.value })}
        disabled={formReadOnly}
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'data-testid': 'quotation-form-date' }}
      />

      <TextField
        select
        label="Cabang"
        required
        fullWidth
        value={company_branch?.documentId ?? ''}
        onChange={(e) => {
          const id = e.target.value;
          const row = branchOptions.find((b) => b.documentId === id) ?? null;
          onPatch({ company_branch: row });
        }}
        disabled={formReadOnly || companyBranchesQ.isPending}
        error={Boolean(companyBranchError)}
        helperText={
          companyBranchError ??
          (companyBranchesQ.isError
            ? 'Gagal memuat daftar cabang.'
            : 'Kode job (GL / GLJ / GLM) menentukan awalan nomor pekerjaan saat work order dibuat.')
        }
        SelectProps={{
          displayEmpty: true,
          // MUI typing for SelectDisplayProps is too strict for data-* attributes.
          SelectDisplayProps: { 'data-testid': 'quotation-form-cabang-trigger' } as unknown as React.HTMLAttributes<HTMLDivElement>,
        }}
        inputProps={{ 'data-testid': 'quotation-form-cabang' }}
      >
        <MenuItem value="" disabled data-testid="quotation-form-cabang-option-empty">
          {companyBranchesQ.isPending ? 'Memuat cabang…' : 'Pilih cabang'}
        </MenuItem>
        {branchOptions.map((b) => (
          <MenuItem
            key={b.documentId}
            value={b.documentId}
            data-testid={`quotation-form-cabang-option-${b.documentId}`}
          >
            {formatCompanyBranchLabel(b)}
          </MenuItem>
        ))}
      </TextField>
      {company_branch?.address?.trim() ? (
        <Typography variant="caption" color="text.secondary" display="block">
          Alamat: {company_branch.address.trim()}
        </Typography>
      ) : null}

      <CustomerSearchAutocomplete
        jwt={jwt}
        value={customer}
        onChange={(v) => onPatch({ customer: v })}
        disabled={customerSelectDisabled || formReadOnly}
        error={customerError}
        required
        inputTestId="quotation-form-customer"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={is_contract}
            onChange={(e) => onPatchContractFlags({ is_contract: e.target.checked })}
            disabled={formReadOnly}
            slotProps={{ input: { 'data-testid': 'quotation-form-is-contract' } as React.InputHTMLAttributes<HTMLInputElement> }}
          />
        }
        label="Kontrak (non kontrak jika tidak dicentang)"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={is_consultant_quote}
            onChange={(e) => onPatchContractFlags({ is_consultant_quote: e.target.checked })}
            disabled={formReadOnly}
            slotProps={{ input: { 'data-testid': 'quotation-form-is-consultant' } as React.InputHTMLAttributes<HTMLInputElement> }}
          />
        }
        label="Penawaran konsultan"
      />

      {is_contract ? (
        <>
          <TextField
            label="Tanggal mulai kontrak"
            type="datetime-local"
            required
            fullWidth
            value={contract_period_start_date}
            onChange={(e) => onPatch({ contract_period_start_date: e.target.value })}
            disabled={formReadOnly}
            error={Boolean(contractPeriodStartDateError)}
            helperText={contractPeriodStartDateError}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'quotation-form-contract-period-start' }}
          />
          <TextField
            label="Tanggal kontrak selesai"
            type="datetime-local"
            required
            fullWidth
            value={contract_period_end_date}
            onChange={(e) => onPatch({ contract_period_end_date: e.target.value })}
            disabled={formReadOnly}
            error={Boolean(contractPeriodEndDateError)}
            helperText={
              contractPeriodEndDateError ??
              'Durasi kontrak dihitung dari selisih tanggal mulai dan tanggal selesai.'
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'quotation-form-contract-period-end' }}
          />
        </>
      ) : null}

      {usesSampleGroups && !formReadOnly ? (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddSampleGroup}
          data-testid="quotation-form-add-sample-group"
        >
          Tambah Sample
        </Button>
      ) : null}

      <FormControlLabel
        control={
          <Checkbox
            checked={preorder}
            onChange={(e) => onPatch({ preorder: e.target.checked })}
            disabled={formReadOnly}
            slotProps={{ input: { 'data-testid': 'quotation-form-preorder' } as React.InputHTMLAttributes<HTMLInputElement> }}
          />
        }
        label="Preorder"
      />

      {hideStatusField ? (
        <TextField
          label="Status quotation"
          fullWidth
          value={quotationStatusDisplayLabel({ progress_status, edit_request_status })}
          disabled
          inputProps={{ 'data-testid': 'quotation-form-status-readonly' }}
        />
      ) : (
        <TextField
          select
          label="Status quotation"
          fullWidth
          value={progress_status}
          onChange={(e) => onPatch({ progress_status: e.target.value as QuotationFormValues['progress_status'] })}
          disabled={formReadOnly}
          inputProps={{ 'data-testid': 'quotation-form-status' }}
        >
          {QUOTATION_STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        label="Metode ambil sampel"
        fullWidth
        value={sample_pickup_method}
        onChange={(e) =>
          onPatch({
            sample_pickup_method: e.target.value as QuotationFormValues['sample_pickup_method'],
          })
        }
        disabled={formReadOnly}
        inputProps={{ 'data-testid': 'quotation-form-sample-pickup' }}
      >
        <MenuItem value="sampling">Sampling</MenuItem>
        <MenuItem value="diantar">Diantar</MenuItem>
      </TextField>

      <FormControlLabel
        control={
          <Checkbox
            checked={term_of_payment}
            onChange={(e) => {
              const checked = e.target.checked;
              onPatch({
                term_of_payment: checked,
                ...(checked
                  ? {}
                  : { term_of_payment_date: '', term_of_payment_note: '' }),
              });
            }}
            disabled={formReadOnly}
            slotProps={{
              input: {
                'data-testid': 'quotation-form-term-of-payment',
              } as React.InputHTMLAttributes<HTMLInputElement>,
            }}
          />
        }
        label="Term of payment"
      />

      {term_of_payment ? (
        <>
          <TextField
            label="Term of payment date"
            type="datetime-local"
            fullWidth
            required
            value={term_of_payment_date}
            onChange={(e) => onPatch({ term_of_payment_date: e.target.value })}
            disabled={formReadOnly}
            error={Boolean(termOfPaymentDateError)}
            helperText={termOfPaymentDateError}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'quotation-form-term-of-payment-date' }}
          />
          <TextField
            label="Term of payment note"
            fullWidth
            multiline
            minRows={2}
            value={term_of_payment_note}
            onChange={(e) => onPatch({ term_of_payment_note: e.target.value })}
            disabled={formReadOnly}
            inputProps={{ 'data-testid': 'quotation-form-term-of-payment-note' }}
          />
        </>
      ) : null}

      <Autocomplete
        options={assigneeOptions}
        loading={assigneeSearchOk && assigneeUsersQ.isFetching}
        value={assignee_user}
        onChange={(_, v) => onPatch({ assignee_user: v })}
        disabled={formReadOnly}
        inputValue={assigneeInput}
        onInputChange={(_, v, reason) => {
          if (reason === 'reset') return;
          setAssigneeInput(v);
        }}
        getOptionLabel={(o) => userOptionLabel(o)}
        isOptionEqualToValue={(a, b) => (a.documentId && b.documentId ? a.documentId === b.documentId : a.id === b.id)}
        filterOptions={(x) => x}
        noOptionsText={
          assigneeSearchOk
            ? assigneeUsersQ.isFetching
              ? 'Memuat…'
              : 'Tidak ada user.'
            : `Ketik minimal ${minUserSearchLen} karakter untuk mencari user aktif.`
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Penanggung jawab (assignee)"
            helperText="User aktif; cari berdasarkan username atau email."
          />
        )}
      />

      <Autocomplete
        options={approverOptions}
        loading={approverSearchOk && approverUsersQ.isFetching}
        value={approved_by_user}
        onChange={(_, v) => onPatch({ approved_by_user: v })}
        disabled={formReadOnly}
        inputValue={approverInput}
        onInputChange={(_, v, reason) => {
          if (reason === 'reset') return;
          setApproverInput(v);
        }}
        getOptionLabel={(o) => userOptionLabel(o)}
        isOptionEqualToValue={(a, b) => (a.documentId && b.documentId ? a.documentId === b.documentId : a.id === b.id)}
        filterOptions={(x) => x}
        noOptionsText={
          approverSearchOk
            ? approverUsersQ.isFetching
              ? 'Memuat…'
              : 'Tidak ada user.'
            : `Ketik minimal ${minUserSearchLen} karakter untuk mencari user aktif.`
        }
        renderInput={(params) => (
          <TextField {...params} label="Disetujui oleh (opsional)" helperText="User yang menyetujui penawaran." />
        )}
      />
    </Stack>
  );
}
